import { ArrowDown, ArrowUp, ChevronDown, Trash2 } from 'lucide-react';
import { useId, useRef, useState, type ReactNode } from 'react';
import './PageEditorFields.css';

export function EditorFieldGroup({ title, children }: { title: string; children: ReactNode }) {
  return (
    <fieldset className="page-field-group">
      <legend>{title}</legend>
      <div className="page-field-group__body">{children}</div>
    </fieldset>
  );
}

export function EditorCollectionActions({
  index,
  count,
  onMove,
  onRemove,
}: {
  index: number;
  count: number;
  onMove(direction: -1 | 1): void;
  onRemove(): void;
}) {
  return (
    <span className="page-item-actions">
      <button
        type="button"
        aria-label="Up"
        title="Move up"
        disabled={index === 0}
        onClick={() => onMove(-1)}
      >
        <ArrowUp size={17} aria-hidden="true" />
      </button>
      <button
        type="button"
        aria-label="Down"
        title="Move down"
        disabled={index === count - 1}
        onClick={() => onMove(1)}
      >
        <ArrowDown size={17} aria-hidden="true" />
      </button>
      <button
        type="button"
        aria-label="Remove"
        title="Remove item"
        disabled={count <= 1}
        onClick={onRemove}
      >
        <Trash2 size={17} aria-hidden="true" />
      </button>
    </span>
  );
}

export function EditorCollectionItem({
  label,
  title,
  summary,
  index,
  count,
  onMove,
  onRemove,
  children,
}: {
  label: string;
  title: string;
  summary?: string;
  index: number;
  count: number;
  onMove(direction: -1 | 1): void;
  onRemove(): void;
  children: ReactNode;
}) {
  const [expanded, setExpanded] = useState(true);
  const body = useRef<HTMLDivElement>(null);
  const id = useId();
  const toggle = () => {
    const active = document.activeElement;
    if (expanded && active instanceof HTMLElement && body.current?.contains(active)) active.blur();
    setExpanded((value) => !value);
  };
  return (
    <article className="page-collection-item">
      <header className="page-collection-item__header">
        <button
          className="page-collection-item__toggle"
          type="button"
          aria-label={`${label}: ${title}`}
          aria-expanded={expanded}
          aria-controls={id}
          onClick={toggle}
        >
          <span>
            <small>{label}</small>
            <strong>{title}</strong>
          </span>
          <ChevronDown size={18} aria-hidden="true" />
        </button>
        <EditorCollectionActions index={index} count={count} onMove={onMove} onRemove={onRemove} />
      </header>
      {expanded ? (
        <div className="page-collection-item__body" id={id} ref={body}>
          {children}
        </div>
      ) : (
        <div id={id} className="page-collection-item__summary">
          {summary}
        </div>
      )}
    </article>
  );
}
